/**
 * Hairline-rule lists — rows separated by a rule, edge to edge (D-1).
 *
 * The Ledger's anatomy, for any list: no container, no gap between rows, a
 * hairline under each, the whole row the touch target (W-8). A row is a
 * `button` when it does something and a plain item when it does not, so it is
 * never a fake control.
 */

import type { ReactNode } from "react";

import { cx } from "@/features/common/primitives";

export function RuleList({
  children,
  label,
  className,
}: {
  children: ReactNode;
  /** Names the list for assistive technology when no heading does. */
  label?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <ul aria-label={label} className={cx("border-t border-line", className)}>
      {children}
    </ul>
  );
}

export function RuleRow({
  title,
  detail,
  leading,
  trailing,
  onClick,
  selected = false,
  children,
  testId,
}: {
  title: ReactNode;
  /** A second, quieter line. */
  detail?: ReactNode;
  /** A glyph or a dot before the text. */
  leading?: ReactNode;
  /** A figure or a control after it. Figures are set in the figure face by the caller. */
  trailing?: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  /** Optional controls below the reading, such as draft category choices. */
  children?: ReactNode;
  testId?: string;
}): React.JSX.Element {
  const body = (
    <>
      {leading ? <span className="shrink-0">{leading}</span> : null}
      <span className="min-w-0 flex-1 basis-1/3">
        <span className="block text-phi-0 font-semibold [overflow-wrap:anywhere]">{title}</span>
        {detail ? <span className="block text-phi-n1 text-tx-2 [overflow-wrap:anywhere]">{detail}</span> : null}
      </span>
      {trailing ? <span className="max-w-full shrink-0 text-right [overflow-wrap:anywhere]">{trailing}</span> : null}
    </>
  );
  const shared = cx(
    "flex min-h-[44px] w-full flex-wrap items-center gap-phi-n2 px-phi-1 py-phi-n2 text-left",
    selected && "bg-acc-soft",
  );
  return (
    <li data-testid={testId} className="border-b border-line">
      {onClick ? (
        <button type="button" onClick={onClick} aria-pressed={selected || undefined} className={cx(shared, "active:bg-bg-3")}>
          {body}
        </button>
      ) : (
        <div className={shared}>{body}</div>
      )}
      {children}
    </li>
  );
}
